import { Routes } from "@angular/router";
import { loadTranslationResolver } from "@app/kernel/translations/resolvers/load-translations.resolver";
import { COMMON_GUARD_OPTIONS } from "@app/shared/auth/constants/permissions.constant";
import { AuthGuard } from "@app/shared/auth/guards/auth.guard";
import { VehicleTypeApiService } from "./domains/vehicle-type/services/vehicle-type-api.service";
import { ParentGroupApiService } from "./domains/parent-group/services/parent-group-api.service";
import { TRANSLATION_PATHS } from "@app/shared/general/translation/constants/translation.constant";
import { FeatureApiService } from "@app/applications/fleet/shared/features/services/feature-api.service";
import { FuelTypeApiService } from "@app/applications/fleet/shared/fuel-type/services/fuel-type-api.service";
import { TireTypeApiService } from "@app/applications/fleet/domains/tire-types/services/tire-type-api.service";
import { TireBrandApiService } from "@app/applications/fleet/domains/tire-brands/services/tire-brand-api.service";
import { CargoCategoryApiService } from "./domains/cargo-categories/services/cargo-category-api.service";
import { VehicleFileApiService } from "@app/applications/fleet/shared/vehicle-file/services/vehicle-file-api.service";
import { SupplyTypeApiService } from "./domains/supply-types/services/supply-type-api.service";
import { LeasingTypeApiService } from "./domains/leasing-types/services/leasing-type-api.service";
import { GroupApiService } from "@app/shared/fleet/group/services/group-api.service";
import { PropertyTypeApiService } from "./domains/property-types/services/property-type-api.service";
import { PostRentingTypeApiService } from "./domains/post-renting-types/services/post-renting-type-api.service";
import { SupplyApiService } from "./domains/supplies/services/supply-api.service";
import { FuelPriceLogApiService } from "./domains/fuel-price-logs/services/fuel-price-log-api.service";
import { VehicleApiService } from "@app/shared/fleet/vehicle/services/vehicle-api.service";
import { IncidentApiService } from "./domains/incidents/services/incident-api.service";
import { FleetCheckApiService } from "./domains/fleet-check/services/fleet-check-api.service";
import { TireModelApiService } from "./domains/tire-models/services/tire-model-api.service";
import { TireCatalogApiService } from "./domains/tire-catalogs/services/tire-catalog-api.service";
import { ElementApiService } from "./domains/element/services/element-api.service";
import { ElementComponentApiService } from "./domains/element-component/services/element-component-api.service";
import { ElementCategoryApiService } from "./domains/element-categories/services/element-category-api.service";
import { DamageTypeApiService } from "./shared/damage-types/services/damage-type-api.service";
import { DamageLevelApiService } from "./shared/damage-levels/services/damage-level-api.service";
import { DiagramApiService } from "./shared/diagram/services/diagram-api.service";
import { DamageApiService } from "@app/shared/fleet/damage/services/damage-api.service";
import { EvaluationReportApiService } from "./shared/evaluation-report/services/evaluation-report-api.service";

export const fleetRoutes: Routes = [
  {
    path: "vehicle-types",
    loadChildren: () => import("./domains/vehicle-type/vehicle-type.routes").then(r => r.vehicleTypesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.VEHICLE_TYPE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.VEHICLE_TYPE]) },
    providers: [VehicleTypeApiService],
  },
  {
    path: "parent-groups",
    loadChildren: () => import("./domains/parent-group/parent-group.routes").then(r => r.parentGroupsRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.COMPANY.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.PARENT_GROUP]) },
    providers: [ParentGroupApiService],
  },
  {
    path: "features",
    loadChildren: () => import("./domains/features/features.routes").then(r => r.featuresRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.FEATURE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.FEATURES]) },
    providers: [FeatureApiService],
  },
  {
    path: "fuel-types",
    loadChildren: () => import("./domains/fuel-types/fuel-types.routes").then(r => r.fuelTypesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.FUEL_TYPE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.FUEL_TYPES]) },
    providers: [FuelTypeApiService],
  },
  {
    path: "tire-types",
    loadChildren: () => import("./domains/tire-types/tire-types.routes").then(r => r.tireTypesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.TIRE_TYPE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.TIRE_TYPES]) },
    providers: [TireTypeApiService],
  },
  {
    path: "tire-brands",
    loadChildren: () => import("./domains/tire-brands/tire-brands.routes").then(r => r.tireBrandsRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.TIRE_BRAND.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.TIRE_BRANDS]) },
    providers: [TireBrandApiService],
  },
  {
    path: "tire-models",
    loadChildren: () => import("./domains/tire-models/tire-models.routes").then(r => r.tireModelsRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.TIRE_MODEL.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.TIRE_MODELS]) },
    providers: [TireModelApiService],
  },
  {
    path: "tire-catalogs",
    loadChildren: () => import("./domains/tire-catalogs/tire-catalogs.routes").then(r => r.tireCatalogsRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.TIRE_CATALOG.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.TIRE_CATALOGS]) },
    providers: [TireCatalogApiService],
  },
  {
    path: "cargo-categories",
    loadChildren: () => import("./domains/cargo-categories/cargo-categories.routes").then(r => r.cargoCategoriesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.CARGO_CATEGORY.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.CARGO_CATEGORY]) },
    providers: [CargoCategoryApiService],
  },
  {
    path: "vehicle-files",
    loadChildren: () => import("./domains/vehicle-files/vehicle-files.routes").then(r => r.vehicleFilesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.VEHICLE_FILE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.VEHICLE_FILES]) },
    providers: [VehicleFileApiService, VehicleApiService],
  },
  {
    path: "vehicles",
    loadChildren: () => import("./domains/vehicles/vehicles.routes").then(r => r.vehicleRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.VEHICLE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.VEHICLES]) },
    providers: [VehicleApiService, FeatureApiService, FuelTypeApiService, VehicleFileApiService],
  },
  {
    path: "supply-types",
    loadChildren: () => import("./domains/supply-types/supply-types.routes").then(r => r.supplyTypesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.SUPPLY_TYPE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.SUPPLY_TYPES]) },
    providers: [SupplyTypeApiService],
  },
  {
    path: "damage-types",
    loadChildren: () => import("./domains/damage-types/damage-types.routes").then(r => r.damageTypesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.DAMAGE_TYPE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.DAMAGE_TYPES]) },
    providers: [DamageTypeApiService],
  },
  {
    path: "damage-levels",
    loadChildren: () => import("./domains/damage-levels/damage-levels.routes").then(r => r.damageLevelsRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.DAMAGE_LEVEL.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.DAMAGE_LEVELS]) },
    providers: [DamageLevelApiService],
  },

  {
    path: "element-categories",
    loadChildren: () => import("./domains/element-categories/element-categories.routes").then(r => r.elementCategoriesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.ELEMENT_CATEGORY.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.ELEMENT_CATEGORY]) },
    providers: [ElementCategoryApiService],
  },

  {
    path: "leasing-types",
    loadChildren: () => import("./domains/leasing-types/leasing-types.routes").then(r => r.leasingTypesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.LEASING_TYPE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.LEASING_TYPES]) },
    providers: [LeasingTypeApiService],
  },
  {
    path: "property-types",
    loadChildren: () => import("./domains/property-types/property-types.routes").then(r => r.propertyTypesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.PROPERTY_TYPE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.PROPERTY_TYPES]) },
    providers: [PropertyTypeApiService],
  },
  {
    path: "post-renting-types",
    loadChildren: () => import("./domains/post-renting-types/post-renting-types.routes").then(r => r.postRentingTypesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.POST_RENTING_TYPE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.POST_RENTING_TYPES]) },
    providers: [PostRentingTypeApiService],
  },
  {
    path: "supplies",
    loadChildren: () => import("./domains/supplies/supplies.routes").then(r => r.suppliesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.SUPPLY.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.SUPPLIES]) },
    providers: [SupplyApiService],
  },
  {
    path: "damage",
    loadChildren: () => import("./domains/damages/damages.routes").then(r => r.damagesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.DAMAGE.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.DAMAGES]) },
    providers: [DamageApiService],
  },
  {
    path: "groups",
    loadChildren: () => import("./domains/group/group.routes").then(r => r.groupRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.GROUP.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.GROUP]) },
    providers: [GroupApiService],
  },
  {
    path: "fuel-price-logs",
    loadChildren: () => import("./domains/fuel-price-logs/fuel-price-logs.routes").then(r => r.fuelPriceLogsRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.FUEL_PRICE_LOG.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.SHARED, TRANSLATION_PATHS.FLEET.FUEL_PRICE_LOGS]) },
    providers: [FuelPriceLogApiService],
  },
  {
    path: "incidents",
    loadChildren: () => import("./domains/incidents/incidents.routes").then(r => r.incidentsRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.INCIDENT.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.INCIDENTS]) },
    providers: [IncidentApiService],
  },
  {
    path: "fleet-check",
    loadChildren: () => import("./domains/fleet-check/fleet-check.routes").then(r => r.fleetCheckRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.FLEET_CHECK.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.FLEET_CHECK]) },
    providers: [FleetCheckApiService],
  },
  {
    path: "diagram",
    loadChildren: () => import("./domains/diagram/diagram.routes").then(r => r.diagramRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.DIAGRAM.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.DIAGRAM]) },
    providers: [DiagramApiService],
  },
  {
    path: "element",
    loadChildren: () => import("./domains/element/element.routes").then(r => r.elementRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.ELEMENT.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.ELEMENT, TRANSLATION_PATHS.FLEET.COST_MATRIX]) },
    providers: [ElementApiService],
  },
  {
    path: "element-component",
    loadChildren: () => import("./domains/element-component/element-component.routes").then(r => r.elementComponentRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.ELEMENT_COMPONENT.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.ELEMENT_COMPONENT, TRANSLATION_PATHS.FLEET.COST_MATRIX]) },
    providers: [ElementComponentApiService],
  },
  {
    path: "diagram-cost-matrix",
    loadChildren: () => import("./domains/diagram-cost-matrix/diagram-cost-matrix.routes").then(r => r.diagramCostMatrixRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.DIAGRAM_COST_MATRIX.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.DIAGRAM_COST_MATRIX, TRANSLATION_PATHS.FLEET.COST_MATRIX]) },
    providers: [DiagramApiService],
  },
  {
    path: "evaluation-reports",
    loadChildren: () => import("./domains/evaluation-report/evaluation-reports.routes").then(r => r.evaluationReportsRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.EVALUATION_REPORT.VIEW },
    resolve: { translation: loadTranslationResolver([TRANSLATION_PATHS.FLEET.EVALUATION_REPORT, TRANSLATION_PATHS.FLEET.DAMAGES]) },
    providers: [EvaluationReportApiService],
  },
];
